import React, { ReactNode, useState, useEffect } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box } from '@mui/system';
import { accordionStyles } from './styles';
import AccordionComponentsTitle from './AccordionComponentsTitle';
import usePractitionerControlsContext from '../../../hooks/usePractitionerControlsContext/usePractitionerControlsContext';

interface AccordionProps {
  className?: string;
  title: string;
  subtitle?: string;
  Icon?: React.ReactNode;
  children?: React.ReactNode;
  isClientPreview?: boolean;
  toggleValue?: boolean;
  setToggle?: Function;
  isIcon?: boolean;
  isExpanded?: boolean;
  pwid?: string;
  // Overrides the default `theme === child` check for the client-preview's child
  // background. Lets the footer hide the landscape when the child game has been
  // promoted to the main area (otherwise the landscape would show in both places).
  childThemeActive?: boolean;
}

const AccordionComponent: React.FC<AccordionProps> = (props) => {
  const {
    title,
    subtitle,
    Icon,
    children,
    className,
    isClientPreview = false,
    toggleValue,
    setToggle,
    isIcon = true,
    isExpanded = false,
    pwid,
    childThemeActive,
  } = props;
  const { container } = accordionStyles;
  // Load initial state from localStorage (if available)
  const [expanded, setExpanded] = useState(() => {
    const storedState = localStorage.getItem(`accordion_${title}`);
    return storedState ? JSON.parse(storedState) : isExpanded;
  });
  const { theme } = usePractitionerControlsContext();
  // Caller may override; otherwise fall back to the raw child-theme flag.
  const showChildTheme = childThemeActive ?? !!theme;
  // Update localStorage whenever the accordion state changes
  useEffect(() => {
    localStorage.setItem(`accordion_${title}`, JSON.stringify(expanded));
  }, [expanded, title]);

  const toggleHandler = () => {
    if (setToggle) {
      setToggle();
    }
    setExpanded(!expanded);
  };

  return (
    <Box key={title} sx={container} className={className}>
      <Accordion
        onChange={toggleHandler}
        expanded={expanded}
        className={`accordion ${isClientPreview ? 'client-preview-accordion' : ''}`}
      >
        <AccordionSummary
          className={`accordion-summary ${
            !isClientPreview ? 'accordion-summary-background' : 'accordion-summary-client-background'
          }`}
          expandIcon={
            <KeyboardArrowDownIcon className={`arrow ${isClientPreview ? 'arrow-icon' : ''}`} fontSize="large" />
          }
          aria-controls={pwid ? `${pwid}-content` : 'panel1-content'}
          id={pwid ?? 'panel1-header'}
          data-testid={pwid}
        >
          {isIcon && <Box className="summary-icon-container">{Icon}</Box>}
          {/* Assuming you have a component for rendering the title and subtitle */}
          <AccordionComponentsTitle title={title} subtitle={subtitle} />
        </AccordionSummary>
        <AccordionDetails
          className={
            title === 'notes'
              ? 'notes'
              : !isClientPreview
                ? 'accordion-details'
                : 'accordion-details-client-view' + (showChildTheme ? ' child-theme' : '')
          }
          id={pwid ? `${pwid}-content` : undefined}
          data-testid={pwid ? `${pwid}-content` : undefined}
        >
          {children}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default AccordionComponent;
